import { booleanAttribute, Component, computed, input, output } from "@angular/core";

import { TimelineBar } from "../timeline-bar/timeline-bar.component";
import { TimelineChip } from "../timeline-chip/timeline-chip.component";
import { addDays, DayKey, formatDayLong, parseDay, weekdayShort } from "../timeline-dates";
import { chipsOn, layoutBars } from "../timeline-layout";
import { TimelineEntry } from "../timeline.model";

/**
 * Phone view: a 7-day strip with colored dots, multi-day bars under it, then the agenda for the week (or the tapped
 * day). Builds and vendor orders are listed on their start day with the range as the second line.
 */
@Component({
  selector: "lib-timeline-agenda",
  templateUrl: "./timeline-agenda.component.html",
  styleUrl: "./timeline-agenda.component.scss",
  imports: [TimelineBar, TimelineChip],
})
export class TimelineAgenda {
  readonly entries = input.required<TimelineEntry[]>();
  readonly start = input.required<DayKey>();
  readonly days = input(7);
  readonly today = input.required<DayKey>();
  readonly selectedId = input<string | null>(null);
  readonly selectedDay = input<DayKey | null>(null);
  /** Hide the strip and bars (phone Month already shows the grid). */
  readonly listOnly = input(false, { transform: booleanAttribute });
  readonly entrySelected = output<string>();
  readonly daySelected = output<DayKey>();

  protected readonly strip = computed(() =>
    Array.from({ length: this.days() }, (_, i) => {
      const day = addDays(this.start(), i);
      const onDay = this.entries().filter((entry) => entry.start <= day && entry.end >= day);
      return {
        day,
        weekday: weekdayShort(day),
        date: parseDay(day).getDate(),
        today: day === this.today(),
        dots: onDay.slice(0, 4).map((entry) => entry.color),
        label: `${formatDayLong(day)}, ${onDay.length} ${onDay.length === 1 ? "entry" : "entries"}`,
      };
    })
  );

  protected readonly bars = computed(() => layoutBars(this.entries(), this.start(), this.days()));

  protected readonly agenda = computed(() => {
    const selected = this.selectedDay();
    const days = selected ? [selected] : this.strip().map((cell) => cell.day);
    const first = this.start();
    return days
      .map((day) => {
        const chips = chipsOn(this.entries(), day);
        // A bar shows on its start day, or on the first day of the week when it started earlier.
        const bars = this.entries().filter(
          (entry) =>
            entry.bar && (entry.start === day || (day === first && !selected && entry.start < day && entry.end >= day))
        );
        return { day, title: formatDayLong(day), items: [...chips, ...bars] };
      })
      .filter((group) => group.items.length || selected);
  });

  protected rangeText(entry: TimelineEntry): string {
    return `${formatDayLong(entry.start)} – ${formatDayLong(entry.end)}`;
  }
}
